import { createIntl, createIntlCache } from 'react-intl';
import { AnyAction, Dispatch, MiddlewareAPI } from 'redux';
import { isActionOf } from 'typesafe-actions';
import { messagesCache } from '../../containers/App/ConnectedIntlProvider';
import { notify } from '../../features/toaster';
import { defaultLocales } from '../../i18n';
import { IntlLocale, supportedLocales } from '../../i18n/config';
import { Next } from '../../types/redux';
import { State } from '../../types/state';
import { throwError, throwInfo } from '../modules/messages/actions';
import { RootActionType } from '../rootActions';

const cache = createIntlCache();

const initIntl = (locale: IntlLocale) => {
  return createIntl(
    { locale, messages: messagesCache[locale] ?? defaultLocales },
    cache
  );
};

const isIntlLocale = (value: string | null | undefined): value is IntlLocale =>
  value ? value in supportedLocales : false;

export default (store: MiddlewareAPI<Dispatch<AnyAction>, State>) =>
  (next: Next) =>
  (action: RootActionType) => {
    if (isActionOf([throwInfo, throwError])(action)) {
      const state = store.getState();

      const intlLocale = isIntlLocale(state.user.uiSettings.locale)
        ? state.user.uiSettings.locale
        : 'en';

      const intl = initIntl(intlLocale);

      const {
        id,
        values,
        options: { isRefreshAction, isPersistent, deduplicate },
      } = action.payload;

      // Toast messages are simple strings without rich text formatting
      const message = intl.formatMessage(
        {
          id: `containers.message.${id}`,
        },
        values
      ) as string;

      notify[action.meta.type](
        {
          title: message,
          // the only buttonFunction instance I found is for refreshing
          toastActions: isRefreshAction
            ? [
                {
                  onClick: () => window.location.reload(),
                  label: intl.formatMessage({
                    id: 'containers.message.newVersion.button',
                  }),
                },
              ]
            : undefined,
        },
        // previous implementation assumes Infinity when timeout is zero
        {
          ...(isPersistent ? { duration: Infinity } : {}),
          ...(deduplicate ? { id } : {}),
          ...(isRefreshAction ? { dismissible: false, id: 'newVersion' } : {}),
        }
      );
    }
    return next(action);
  };
